import { GroupSecurity } from '@quromedical/fhir-common';
import {
  type DeviceCreateRequest,
  type DeviceGetResponse,
  DeviceSchema,
  type DeviceUpdateRequest,
} from '@quromedical/models';
import { CrudForm, type SubmissionHandler } from 'components/form';
import {
  Alert,
  FormCard,
  Skeleton,
  SkeletonProvider,
  Snackbar,
  Visible,
} from 'design-system';
import { logger } from 'helpers';
import { useUserSession } from 'hooks';
import { useFiniteState } from 'hooks/useFiniteState';
import { DeviceApi } from 'integration/aggregate';
import type React from 'react';
import { useCallback, useMemo } from 'react';
import { strings } from 'strings';

import { deviceCardRows, deviceFormFields } from './internal';

interface DeviceCardProps {
  id: string;
  device?: DeviceGetResponse;
  revalidate: () => Promise<void>;
}

type State = 'initial' | 'submitting' | 'error' | 'editing';

const api = new DeviceApi();

export const DeviceCard: React.FC<DeviceCardProps> = ({
  id,
  device,
  revalidate,
}) => {
  const state = useFiniteState<State>('initial');

  const rows = useMemo(() => deviceCardRows(device), [device]);
  const session = useUserSession();
  const canEdit = session.hasAny([GroupSecurity.DeviceUpdate]);

  const onSubmit = useCallback<SubmissionHandler<DeviceCreateRequest>>(
    async (formData: DeviceCreateRequest) => {
      try {
        state.set('submitting');
        await api.updateDevice(id, formData);

        revalidate().catch(logger.error);
        state.set('initial');
        return {};
      } catch (error) {
        state.set('error');
        logger.error(error);
        return { error };
      }
    },
    [id, revalidate, state],
  );

  const initialValues = device || {};

  return (
    <>
      <SkeletonProvider loading={state.is('submitting')}>
        <Skeleton>
          <Visible if={state.isNot('editing')}>
            <FormCard
              onButtonPress={state.next('editing')}
              isButtonVisible={canEdit}
              buttonText={strings.ButtonTextEdit}
              title={strings.DeviceDetails}
              buttonIcon="edit"
              showIcons
              rows={rows}
            />
          </Visible>

          <Visible if={state.is('editing')}>
            <CrudForm<DeviceUpdateRequest>
              cardProps={{ unsetZIndex: true }}
              title={strings.DeviceDetails}
              buttonText={strings.ButtonTextUpdate}
              fields={deviceFormFields}
              initialValues={initialValues}
              includeIcons={true}
              onSubmit={onSubmit}
              onSecondarySubmit={state.next('initial')}
              showSecondarySubmitButton={true}
              validationSchema={DeviceSchema}
            />
          </Visible>
        </Skeleton>
      </SkeletonProvider>

      <Snackbar onClose={state.next('editing')} isOpen={state.is('error')}>
        <Alert
          backgroundColor="status-critical"
          textColor="white"
          onAction={state.next('editing')}
          actionIcon="close"
          title={strings.ErrorCardTitle}
          body={strings.GenericErrorMessage}
        />
      </Snackbar>
    </>
  );
};
